import { createAsyncThunk } from "@reduxjs/toolkit"
import { type AxiosProgressEvent } from "axios"
import type { NavigateFunction } from "react-router/dist/lib/hooks"

import { RoutesPaths } from "../../../constants/routes.ts"
import { selectActiveBranchId } from "../../Branches/redux/branches.slice.ts"
import { rejectWithHandledError } from "../../../utils/handlers/reject-with-handled-error.ts"
import type { RootState } from "../../../store.ts"
import i18n from "../../../i18n.ts"
import { showResponseMessage } from "../../../utils/handlers/show-response-message.ts"
import { type LeadProposal } from "../../Lead/types/lead.documents.types.ts"
import { type ProposalFormData } from "../ProposalAddPage/components/proposal-form-data.tsx"
import * as services from "../api/services"
import { type Proposal, type ProposalListItem } from "../types/types.ts"
import { proposalsSliceName } from "./proposals.constants.ts"
import { type ProposalRequestData } from "./proposals.thunks.types.ts"
import type {
  ProposalFilterOptions,
  ProposalFilters
} from "./proposals.types.ts"

export const fetchProposals = createAsyncThunk<
  ProposalListItem[],
  ProposalFilters | undefined,
  { state: RootState }
>(
  `${proposalsSliceName}/fetchProposals`,
  async (filters, { dispatch, getState, rejectWithValue }) => {
    const state = getState()
    const params = filters || state?.proposals?.filters
    const activeBranchId = selectActiveBranchId(state)
    const response = await services.getProposals({
      ...params,
      ...(activeBranchId ? { team: activeBranchId } : {})
    })
    // const response = await services.getPendingProposals()

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        rejectValue: response.data || { errors: "Unknown error" },
        flow: "proposals.fetchProposals"
      })
    }

    return response?.data as ProposalListItem[]
  }
)

export const fetchProposal = createAsyncThunk<Proposal, string>(
  `${proposalsSliceName}/fetchProposal`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.getProposalById(id)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        rejectValue: response.data || { errors: "Unknown error" },
        flow: "proposals.fetchProposal"
      })
    }

    return response?.data as Proposal
  }
)

export const fetchProposalInitialData = createAsyncThunk<
  ProposalFormData,
  string
>(
  `${proposalsSliceName}/fetchProposalInitialData`,
  async (leadID, { dispatch, rejectWithValue }) => {
    const response = await services.getProposalInitialValues(leadID)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        rejectValue: response.data || { errors: "Unknown error" },
        flow: "proposals.fetchProposalInitialData"
      })
    }

    return response?.data as ProposalFormData
  }
)

export const getProposalPreview = createAsyncThunk<string, ProposalRequestData>(
  `${proposalsSliceName}/previewProposal`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.previewProposal(params)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        rejectValue: null,
        flow: "proposals.getProposalPreview"
      })
    }

    return response?.data as string
  }
)

export const createProposal = createAsyncThunk<string, ProposalRequestData>(
  `${proposalsSliceName}/createProposal`,
  async (data, { dispatch, rejectWithValue }) => {
    const res = await services.createProposal(data)

    if (!res.ok) {
      return rejectWithHandledError(res, {
        dispatch,
        rejectWithValue,
        rejectValue: "",
        flow: "proposals.createProposal"
      })
    }
    const proposalId = res?.data?.details?.data?.id
    if (typeof proposalId !== "string") {
      return rejectWithHandledError(res, {
        dispatch,
        rejectWithValue,
        rejectValue: "Invalid proposal ID",
        code: "malformed",
        flow: "proposals.createProposal"
      })
    }

    return proposalId
  }
)

export const generateProposal = createAsyncThunk<
  any,
  {
    data: ProposalRequestData
    navigate: NavigateFunction
    leadId: string
    proposalId: string
  }
>(
  `${proposalsSliceName}/generateProposal`,
  async (
    { navigate, leadId, data, proposalId },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const generateRes = await services.updateProposal(proposalId, data)
      if (!generateRes.ok) throw new Error()

      const saveRes = await services.saveProposalFile(proposalId)
      if (!saveRes.ok) throw new Error()

      showResponseMessage({}, "Proposal created successfully.")
      navigate(RoutesPaths.lead.view(leadId))

      return saveRes?.data
    } catch {
      return rejectWithHandledError(null, {
        dispatch,
        rejectWithValue,
        rejectValue: null,
        flow: "proposals.generateProposal"
      })
    }
  }
)

export const sendProposalToApproval = createAsyncThunk<
  void,
  {
    data: ProposalRequestData
    navigate: NavigateFunction
    leadId: string
    proposalId: string
  },
  { state: RootState }
>(
  `${proposalsSliceName}/sendToApproval`,
  async (
    { navigate, leadId, data, proposalId },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const updateRes = await services.updateProposal(proposalId, data)
      if (!updateRes.ok) throw new Error()

      const saveRes = await services.saveProposalFile(proposalId)
      if (!saveRes.ok) {
        return rejectWithHandledError(saveRes, {
          dispatch,
          rejectWithValue,
          rejectValue: null,
          flow: "proposals.sendProposalToApproval"
        })
      }

      const attachmentId = (
        saveRes.data as { details?: { data?: { id?: string } } }
      )?.details?.data?.id
      if (!attachmentId) throw new Error()

      const submitRes = await services.submitForReview({
        proposalId,
        attachmentId
      })
      if (!submitRes.ok) {
        return rejectWithHandledError(submitRes, {
          dispatch,
          rejectWithValue,
          rejectValue: null,
          flow: "proposals.sendProposalToApproval"
        })
      }

      showResponseMessage({}, i18n.t("documentApproval.proposalSent"))
      navigate(RoutesPaths.lead.view(leadId))
    } catch {
      return rejectWithHandledError(null, {
        dispatch,
        rejectWithValue,
        rejectValue: null,
        flow: "proposals.sendProposalToApproval"
      })
    }
  }
)

export const saveProposal = createAsyncThunk<
  any,
  { data: any; navigate: NavigateFunction }
>(
  `${proposalsSliceName}/saveProposal`,
  async ({ navigate, data }, { dispatch, rejectWithValue }) => {
    const response = await services.createProposal(data)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        rejectValue: null,
        flow: "proposals.saveProposal"
      })
    }

    showResponseMessage(response, "Proposal created successfully.")
    navigate(RoutesPaths.lead.view(data?.lead_id))

    return response?.data?.details?.data
  }
)

export const updateProposal = createAsyncThunk<
  void,
  {
    data: ProposalRequestData
    proposalId: string
    showSuccessMessage?: boolean
  }
>(
  `${proposalsSliceName}/updateProposal`,
  async (
    { proposalId, data, showSuccessMessage },
    { dispatch, rejectWithValue }
  ) => {
    const response = await services.updateProposal(proposalId, data)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        rejectValue: null,
        flow: "proposals.updateProposal"
      })
    }

    if (showSuccessMessage) {
      showResponseMessage(response, "Proposal updated successfully.")
    }
  }
)

export const submitProposalForReview = createAsyncThunk<
  LeadProposal,
  {
    proposalId: string
    attachmentId: string
  }
>(
  `${proposalsSliceName}/submitForReview`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.submitForReview(data)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        rejectValue: null,
        flow: "proposals.submitProposalForReview"
      })
    }

    showResponseMessage(response, "Proposal submitted for review successfully.")

    return response?.data?.details?.data as LeadProposal
  }
)

export const rejectProposal = createAsyncThunk<
  null,
  { id: string; reason: string }
>(
  `${proposalsSliceName}/rejectProposal`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.rejectProposal(params)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        rejectValue: null,
        flow: "proposals.rejectProposal"
      })
    }

    showResponseMessage(response, "Proposal rejected successfully.")

    return null
  }
)

export const cancelProposal = createAsyncThunk<
  null,
  { id: string; reason: string; notes: string }
>(
  `${proposalsSliceName}/cancelProposal`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.cancelProposal(params)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        rejectValue: null,
        flow: "proposals.cancelProposal"
      })
    }

    showResponseMessage(response, "Proposal cancelled successfully.")

    return null
  }
)

export const approveProposal = createAsyncThunk<null, string>(
  `${proposalsSliceName}/approveProposal`,
  async (proposalId, { dispatch, rejectWithValue }) => {
    const response = await services.approveProposal(proposalId)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        rejectValue: null,
        flow: "proposals.approveProposal"
      })
    }

    showResponseMessage(response, "Proposal approved successfully.")

    return null
  }
)

export const duplicateProposal = createAsyncThunk<LeadProposal, string>(
  `${proposalsSliceName}/duplicateProposal`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.duplicateProposal(data)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        rejectValue: null,
        flow: "proposals.duplicateProposal"
      })
    }

    showResponseMessage({}, "Proposal duplicated successfully.")

    return response?.data?.details?.data as LeadProposal
  }
)

export const uploadProposal = createAsyncThunk<
  LeadProposal,
  {
    file: File
    lead_id: string
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
  }
>(
  `${proposalsSliceName}/uploadProposal`,
  async ({ onUploadProgress, ...data }, { dispatch, rejectWithValue }) => {
    const response = await services.uploadProposal(data, { onUploadProgress })

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        rejectValue: null,
        flow: "proposals.uploadProposal"
      })
    }

    showResponseMessage(response, "Proposal uploaded successfully.")

    return response?.data?.details?.data as LeadProposal
  }
)

export const markProposalAsSent = createAsyncThunk<any, string>(
  `${proposalsSliceName}/markProposalAsSent`,
  async (proposalId, { dispatch, rejectWithValue }) => {
    const response = await services.sentProposalToClient(proposalId)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        rejectValue: null,
        flow: "proposals.markProposalAsSent"
      })
    }

    showResponseMessage(response, "Proposal marked as sent successfully.")

    return response?.data?.details?.data
  }
)

export const emailProposalToClient = createAsyncThunk<LeadProposal, string>(
  `${proposalsSliceName}/emailProposalToClient`,
  async (proposalId, { dispatch, rejectWithValue }) => {
    const response = await services.sendProposalEmail(proposalId)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        rejectValue: null,
        flow: "proposals.emailProposalToClient"
      })
    }

    showResponseMessage(response, "Proposal emailed successfully.")

    return response?.data?.details?.data as LeadProposal
  }
)

export const getProposalFilterOptions = createAsyncThunk<
  ProposalFilterOptions,
  void
>(
  `${proposalsSliceName}/getFilterOptions`,
  async (_, { dispatch, rejectWithValue }) => {
    const response = await services.getFilterOptions()

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        rejectValue: null,
        flow: "proposals.getProposalFilterOptions"
      })
    }

    return response?.data as ProposalFilterOptions
  }
)
